import React, { useCallback, useEffect, useRef } from 'react';
import { animate } from 'animejs';
import { AdPositionBlocks } from 'shared/components/AdPositionBlocks';
import { getBlockTranslates } from './utils';
import { useDeviceType } from '@cian/ui-kit';

interface IAdPositionBlocksContainerProps {
  index: number;
}

export const AdPositionBlocksContainer: React.FC<IAdPositionBlocksContainerProps> = ({ index }) => {
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const stepsRef = useRef<{ stepX: number; stepY: number } | null>(null);
  const prevIndexRef = useRef<number | undefined>(undefined);
  const animationRef = useRef<ReturnType<typeof animate> | null>(null);
  const device = useDeviceType();

  const setBlockRef = useCallback(
    (blockIndex: number) => (node: HTMLDivElement | null) => {
      blockRefs.current[blockIndex] = node;
    },
    [],
  );

  useEffect(() => {
    const isFirstRun = prevIndexRef.current === undefined;

    prevIndexRef.current = index;

    if (isFirstRun) {
      return;
    }

    const refs = blockRefs.current.filter(Boolean) as HTMLDivElement[];

    if (stepsRef.current === null) {
      const r0 = refs[0].getBoundingClientRect();
      const r1 = refs[1].getBoundingClientRect();
      const r2 = refs[2].getBoundingClientRect();
      stepsRef.current = {
        stepX: r1.left - r0.left,
        stepY: r2.top - r0.top,
      };
    }

    const translates = getBlockTranslates(index, stepsRef.current.stepX, stepsRef.current.stepY);

    animationRef.current?.cancel();

    animationRef.current = animate(refs, {
      duration: 400,
      ease: 'outCubic',
      translateX: (_: HTMLElement, i: number) => translates[i].x,
      translateY: (_: HTMLElement, i: number) => translates[i].y,
    });
  }, [index]);

  return <AdPositionBlocks setBlockRef={setBlockRef} device={device} />;
};
